"use client";

import { type FormEvent, useRef, useState } from "react";

import { ApiRequestError } from "@/lib/auth-api";
import { createManualOrder } from "@/lib/order-api";
import type { Incoterm, ManualOrderCreateData, ManualOrderCreateInput, ResponsibilityParty } from "@/lib/order-types";

interface ManualOrderCreateProps {
    csrfToken: string;
    onCancel: () => void;
    onCreated: (created: ManualOrderCreateData) => void;
}

interface ItemForm {
    sku: string;
    productName: string;
    variant: string;
    quantity: string;
    unitPriceMinor: string;
    discountMinor: string;
}

interface OrderForm {
    externalOrderNumber: string;
    customerNameMasked: string;
    customerPhoneMasked: string;
    shippingAddressMasked: string;
    destinationCountryCode: string;
    incoterm: Incoterm;
    taxResponsibility: ResponsibilityParty;
    dutyResponsibility: ResponsibilityParty;
    importerOfRecord: ResponsibilityParty;
    orderDiscountMinor: string;
    shippingMinor: string;
    reason: string;
}

const emptyItem = (): ItemForm => ({
    sku: "",
    productName: "",
    variant: "",
    quantity: "1",
    unitPriceMinor: "",
    discountMinor: "0",
});

const initialForm: OrderForm = {
    externalOrderNumber: "",
    customerNameMasked: "",
    customerPhoneMasked: "",
    shippingAddressMasked: "",
    destinationCountryCode: "US",
    incoterm: "DDP",
    taxResponsibility: "MERCHANT",
    dutyResponsibility: "MERCHANT",
    importerOfRecord: "TO_BE_CONFIRMED",
    orderDiscountMinor: "0",
    shippingMinor: "0",
    reason: "后台手工创建跨境订单",
};

const responsibilityOptions: { value: ResponsibilityParty; label: string }[] = [
    { value: "MERCHANT", label: "商户" },
    { value: "CUSTOMER", label: "客户" },
    { value: "LOGISTICS_PROVIDER", label: "物流服务商" },
    { value: "TO_BE_CONFIRMED", label: "待确认" },
];

export function ManualOrderCreate({ csrfToken, onCancel, onCreated }: ManualOrderCreateProps) {
    const [form, setForm] = useState<OrderForm>(initialForm);
    const [items, setItems] = useState<ItemForm[]>([emptyItem()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const idempotencyKey = useRef<string | null>(null);

    function changeForm<K extends keyof OrderForm>(key: K, value: OrderForm[K]) {
        idempotencyKey.current = null;
        setForm((current) => ({ ...current, [key]: value }));
    }

    function changeItem(index: number, key: keyof ItemForm, value: string) {
        idempotencyKey.current = null;
        setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
    }

    function changeIncoterm(value: Incoterm) {
        idempotencyKey.current = null;
        setForm((current) => ({
            ...current,
            incoterm: value,
            taxResponsibility: value === "DDP" ? "MERCHANT" : "CUSTOMER",
            dutyResponsibility: value === "DDP" ? "MERCHANT" : "CUSTOMER",
            importerOfRecord: value === "DDP" ? "TO_BE_CONFIRMED" : "CUSTOMER",
        }));
    }

    function addItem() {
        idempotencyKey.current = null;
        setItems((current) => [...current, emptyItem()]);
    }

    function removeItem(index: number) {
        idempotencyKey.current = null;
        setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    }

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setError(null);
        let input: ManualOrderCreateInput;
        try {
            input = buildCreateInput(form, items, idempotencyKey.current ?? createIdempotencyKey());
            idempotencyKey.current = input.idempotency_key;
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : "建单内容不合法");
            return;
        }
        setIsSubmitting(true);
        try {
            const created = await createManualOrder(input, csrfToken);
            idempotencyKey.current = null;
            onCreated(created);
        } catch (caught) {
            setError(createErrorMessage(caught));
        } finally {
            setIsSubmitting(false);
        }
    }

    const estimatedTotal = calculateEstimatedTotal(form, items);

    return (
        <section className="manual-order-card card" aria-labelledby="manual-order-title">
            <div className="manual-order-head">
                <div>
                    <p className="section-kicker">MANUAL ORDER · LOCAL SINGLE MERCHANT</p>
                    <h2 id="manual-order-title">新增跨境订单</h2>
                    <p>订单固定为 USD、待支付、待履约；仅接收已经脱敏的客户信息。</p>
                </div>
                <button aria-label="关闭新增订单" onClick={onCancel} type="button">
                    ×
                </button>
            </div>
            <form className="manual-order-form" onSubmit={submit}>
                <fieldset>
                    <legend>客户与来源</legend>
                    <div className="manual-order-grid">
                        <label>
                            <span>外部订单号（可选）</span>
                            <input
                                maxLength={96}
                                onChange={(event) => changeForm("externalOrderNumber", event.target.value)}
                                placeholder="例如 EXT-US-0001"
                                value={form.externalOrderNumber}
                            />
                        </label>
                        <label>
                            <span>客户姓名（必须脱敏）</span>
                            <input
                                maxLength={120}
                                onChange={(event) => changeForm("customerNameMasked", event.target.value)}
                                placeholder="例如 张*"
                                required
                                value={form.customerNameMasked}
                            />
                        </label>
                        <label>
                            <span>联系电话（必须脱敏）</span>
                            <input
                                maxLength={32}
                                onChange={(event) => changeForm("customerPhoneMasked", event.target.value)}
                                placeholder="例如 +1******0100"
                                required
                                value={form.customerPhoneMasked}
                            />
                        </label>
                        <label className="wide">
                            <span>收货地址（必须脱敏）</span>
                            <input
                                maxLength={500}
                                onChange={(event) => changeForm("shippingAddressMasked", event.target.value)}
                                placeholder="例如 California ****"
                                required
                                value={form.shippingAddressMasked}
                            />
                        </label>
                    </div>
                </fieldset>

                <fieldset>
                    <legend>跨境规则</legend>
                    <div className="manual-order-grid three">
                        <label>
                            <span>目的国家/地区</span>
                            <input
                                maxLength={2}
                                minLength={2}
                                onChange={(event) => changeForm("destinationCountryCode", event.target.value.toUpperCase())}
                                pattern="[A-Z]{2}"
                                required
                                value={form.destinationCountryCode}
                            />
                        </label>
                        <label>
                            <span>贸易术语</span>
                            <select onChange={(event) => changeIncoterm(event.target.value as Incoterm)} value={form.incoterm}>
                                <option value="DDP">DDP · 完税后交货</option>
                                <option value="DAP">DAP · 目的地交货</option>
                            </select>
                        </label>
                        <ResponsibilitySelect
                            label="税费承担方"
                            onChange={(value) => changeForm("taxResponsibility", value)}
                            value={form.taxResponsibility}
                        />
                        <ResponsibilitySelect
                            label="关税承担方"
                            onChange={(value) => changeForm("dutyResponsibility", value)}
                            value={form.dutyResponsibility}
                        />
                        <ResponsibilitySelect
                            label="进口申报主体"
                            onChange={(value) => changeForm("importerOfRecord", value)}
                            value={form.importerOfRecord}
                        />
                        <div className="fixed-field">
                            <span>交易 / 结算币种</span>
                            <strong>USD / USD</strong>
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="items-legend">
                        <legend>商品明细</legend>
                        <button className="button secondary compact-button" onClick={addItem} type="button">
                            ＋ 添加商品
                        </button>
                    </div>
                    <div className="manual-items">
                        {items.map((item, index) => (
                            <div className="manual-item" key={index}>
                                <strong>商品 {index + 1}</strong>
                                <label>
                                    <span>SKU</span>
                                    <input onChange={(event) => changeItem(index, "sku", event.target.value)} required value={item.sku} />
                                </label>
                                <label>
                                    <span>商品名称</span>
                                    <input
                                        onChange={(event) => changeItem(index, "productName", event.target.value)}
                                        required
                                        value={item.productName}
                                    />
                                </label>
                                <label>
                                    <span>规格</span>
                                    <input onChange={(event) => changeItem(index, "variant", event.target.value)} value={item.variant} />
                                </label>
                                <label>
                                    <span>数量</span>
                                    <input
                                        min="1"
                                        onChange={(event) => changeItem(index, "quantity", event.target.value)}
                                        required
                                        step="1"
                                        type="number"
                                        value={item.quantity}
                                    />
                                </label>
                                <label>
                                    <span>USD 单价（美分）</span>
                                    <input
                                        min="0"
                                        onChange={(event) => changeItem(index, "unitPriceMinor", event.target.value)}
                                        required
                                        step="1"
                                        type="number"
                                        value={item.unitPriceMinor}
                                    />
                                </label>
                                <label>
                                    <span>行优惠（美分）</span>
                                    <input
                                        min="0"
                                        onChange={(event) => changeItem(index, "discountMinor", event.target.value)}
                                        required
                                        step="1"
                                        type="number"
                                        value={item.discountMinor}
                                    />
                                </label>
                                {items.length > 1 && (
                                    <button className="remove-item" onClick={() => removeItem(index)} type="button">
                                        移除
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </fieldset>

                <fieldset>
                    <legend>金额与审计</legend>
                    <div className="manual-order-grid three">
                        <label>
                            <span>订单优惠（美分）</span>
                            <input
                                min="0"
                                onChange={(event) => changeForm("orderDiscountMinor", event.target.value)}
                                required
                                step="1"
                                type="number"
                                value={form.orderDiscountMinor}
                            />
                        </label>
                        <label>
                            <span>运费（美分）</span>
                            <input
                                min="0"
                                onChange={(event) => changeForm("shippingMinor", event.target.value)}
                                required
                                step="1"
                                type="number"
                                value={form.shippingMinor}
                            />
                        </label>
                        <div className="fixed-field total-preview">
                            <span>预计订单总额</span>
                            <strong>{formatUsd(estimatedTotal)}</strong>
                        </div>
                        <label className="wide">
                            <span>建单原因（审计必填）</span>
                            <textarea
                                maxLength={500}
                                minLength={2}
                                onChange={(event) => changeForm("reason", event.target.value)}
                                required
                                value={form.reason}
                            />
                        </label>
                    </div>
                </fieldset>

                {error && (
                    <div className="form-message error" role="alert">
                        {error}
                    </div>
                )}
                <div className="manual-order-actions">
                    <button className="button secondary" disabled={isSubmitting} onClick={onCancel} type="button">
                        取消
                    </button>
                    <button className="button primary" disabled={isSubmitting} type="submit">
                        {isSubmitting ? "正在创建…" : "确认创建待支付订单"}
                    </button>
                </div>
            </form>
        </section>
    );
}

function ResponsibilitySelect({
    label,
    onChange,
    value,
}: {
    label: string;
    onChange: (value: ResponsibilityParty) => void;
    value: ResponsibilityParty;
}) {
    return (
        <label>
            <span>{label}</span>
            <select onChange={(event) => onChange(event.target.value as ResponsibilityParty)} value={value}>
                {responsibilityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

export function buildCreateInput(form: OrderForm, items: ItemForm[], key: string): ManualOrderCreateInput {
    for (const [label, value] of [
        ["客户姓名", form.customerNameMasked],
        ["联系电话", form.customerPhoneMasked],
        ["收货地址", form.shippingAddressMasked],
    ]) {
        if (!value.includes("*")) throw new Error(`${label}必须先脱敏，并包含 *`);
    }
    const parsedItems = items.map((item, index) => {
        const quantity = parseInteger(`商品 ${index + 1} 数量`, item.quantity, 1);
        const unitPrice = parseInteger(`商品 ${index + 1} 单价`, item.unitPriceMinor, 0);
        const discount = parseInteger(`商品 ${index + 1} 行优惠`, item.discountMinor, 0);
        if (discount > unitPrice * quantity) throw new Error(`商品 ${index + 1} 行优惠不能超过商品金额`);
        return {
            sku: item.sku.trim(),
            product_name: item.productName.trim(),
            variant: item.variant.trim(),
            quantity,
            unit_price_minor: unitPrice,
            discount_minor: discount,
        };
    });
    const subtotal = parsedItems.reduce((sum, item) => sum + item.unit_price_minor * item.quantity - item.discount_minor, 0);
    const orderDiscount = parseInteger("订单优惠", form.orderDiscountMinor, 0);
    if (orderDiscount > subtotal) throw new Error("订单优惠不能超过商品小计");
    return {
        idempotency_key: key,
        reason: form.reason.trim(),
        ...(form.externalOrderNumber.trim() ? { external_order_number: form.externalOrderNumber.trim() } : {}),
        customer_name_masked: form.customerNameMasked.trim(),
        customer_phone_masked: form.customerPhoneMasked.trim(),
        shipping_address_masked: form.shippingAddressMasked.trim(),
        destination_country_code: form.destinationCountryCode,
        incoterm: form.incoterm,
        tax_responsibility: form.taxResponsibility,
        duty_responsibility: form.dutyResponsibility,
        importer_of_record: form.importerOfRecord,
        order_discount_minor: orderDiscount,
        shipping_minor: parseInteger("运费", form.shippingMinor, 0),
        items: parsedItems,
    };
}

function parseInteger(label: string, value: string, minimum: number): number {
    if (!/^\d+$/.test(value)) throw new Error(`${label}必须是整数`);
    const parsed = Number(value);
    if (!Number.isSafeInteger(parsed) || parsed < minimum) throw new Error(`${label}不能小于 ${minimum}`);
    return parsed;
}

function calculateEstimatedTotal(form: OrderForm, items: ItemForm[]): number {
    const subtotal = items.reduce((sum, item) => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unitPriceMinor);
        const discount = Number(item.discountMinor);
        if (![quantity, unitPrice, discount].every(Number.isFinite)) return sum;
        return sum + Math.max(0, unitPrice * quantity - discount);
    }, 0);
    return Math.max(0, subtotal - (Number(form.orderDiscountMinor) || 0) + (Number(form.shippingMinor) || 0));
}

function formatUsd(minor: number): string {
    return new Intl.NumberFormat("zh-CN", { style: "currency", currency: "USD" }).format(minor / 100);
}

function createIdempotencyKey(): string {
    const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}`;
    return `manual-order-ui-${suffix}`;
}

function createErrorMessage(error: unknown): string {
    if (!(error instanceof ApiRequestError)) return "建单失败，请稍后重试";
    const messages: Record<string, string> = {
        EXTERNAL_ORDER_NUMBER_EXISTS: "相同渠道下的外部订单号已经存在",
        IDEMPOTENCY_KEY_REUSED: "本次建单请求标识已被不同内容使用，请修改表单后重试",
        PERMISSION_DENIED: "当前角色没有后台建单权限",
        CSRF_INVALID: "写入令牌已失效，请退出后重新登录",
        VALIDATION_ERROR: "建单内容未通过后端校验，请检查金额、脱敏字段和必填项",
    };
    return messages[error.errorCode] ?? error.message;
}
